import { Temporal } from '@js-temporal/polyfill'

import { calendarDateFromStorage, isCalendarDate } from './calendar-date'

export const WORKFLOW_TIME_UNITS = [
  'minutes',
  'hours',
  'calendar_days',
  'calendar_weeks',
  'months',
  'quarters',
  'years',
  'days',
  'weeks',
] as const
export type WorkflowTimeUnit = (typeof WORKFLOW_TIME_UNITS)[number]
export const WORKFLOW_CALENDAR_UNITS: readonly WorkflowTimeUnit[] = [
  'calendar_days',
  'calendar_weeks',
  'months',
  'quarters',
  'years',
]
export interface WorkflowTimeOptions {
  timezone?: string
  now?: Date
  baseDate?: unknown
}
interface ParsedWorkflowTimeExpression {
  direction?: 'add' | 'subtract'
  unit?: WorkflowTimeUnit
  amount?: number
  basePath?: string
  baseKind?: 'date' | 'instant'
}
const TIME_TOKEN_PATTERN =
  /^time\.(now|(?:(add|subtract)\.(minutes|hours|days|weeks|calendar_days|calendar_weeks|months|quarters|years)\.(-?\d+(?:\.\d+)?)(?:\.(from|fromDate)\.(.+))?))$/
const ELAPSED: Partial<Record<WorkflowTimeUnit, number>> = {
  minutes: 60_000,
  hours: 3_600_000,
  days: 86_400_000,
  weeks: 604_800_000,
}

export function workflowTimeZone(value: unknown = 'UTC'): string {
  if (typeof value !== 'string' || !value || /^[+-]/.test(value))
    throw new RangeError('Choose a valid workflow timezone')
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: value })
  } catch {
    throw new RangeError('Choose a valid workflow timezone')
  }
  return value
}

/** Match the persisted organization timezone precedence on every workflow surface. */
export function workflowTimeZoneFromSettings(settings: unknown): string {
  const config = settings as {
    timezone?: unknown
    timeZone?: unknown
    general?: { timezone?: unknown; timeZone?: unknown }
  } | null
  return workflowTimeZone(
    config?.timezone ??
      config?.timeZone ??
      config?.general?.timezone ??
      config?.general?.timeZone ??
      'UTC',
  )
}

export function isWorkflowTimeExpression(token: string): boolean {
  return token.trim().startsWith('time.')
}
export function parseWorkflowTimeExpression(
  token: string,
): ParsedWorkflowTimeExpression {
  const match = TIME_TOKEN_PATTERN.exec(token.trim())
  if (!match) throw new RangeError('Invalid workflow time expression')
  if (match[1] === 'now') return {}
  const unit = match[3] as WorkflowTimeUnit
  const amount = Number(match[4])
  const basePath = match[6]?.trim()
  if (!Number.isFinite(amount))
    throw new RangeError('Invalid workflow time amount')
  if (WORKFLOW_CALENDAR_UNITS.includes(unit) && !Number.isSafeInteger(amount))
    throw new RangeError(
      `Workflow time unit "${unit}" requires a whole-number amount`,
    )
  if (
    basePath &&
    (!/^(event|payload)\./.test(basePath) ||
      basePath
        .split('.')
        .some(
          part =>
            !part || ['__proto__', 'prototype', 'constructor'].includes(part),
        ))
  )
    throw new RangeError('Choose a valid workflow date field')
  return {
    direction: match[2] as 'add' | 'subtract',
    unit,
    amount,
    basePath,
    baseKind: match[5] === 'fromDate' ? 'date' : 'instant',
  }
}
export function getWorkflowTimeExpressionBasePath(
  token: string,
): string | undefined {
  return parseWorkflowTimeExpression(token).basePath
}

function calendarDuration(parsed: ParsedWorkflowTimeExpression) {
  const amount =
    (parsed.direction === 'subtract' ? -1 : 1) * (parsed.amount ?? 0)
  switch (parsed.unit) {
    case 'calendar_days':
      return { days: amount }
    case 'calendar_weeks':
      return { weeks: amount }
    case 'months':
      return { months: amount }
    case 'quarters':
      return { months: amount * 3 }
    case 'years':
      return { years: amount }
    default:
      return {}
  }
}
function baseValue(
  parsed: ParsedWorkflowTimeExpression,
  options: WorkflowTimeOptions,
): unknown {
  const value = parsed.basePath ? options.baseDate : (options.now ?? new Date())
  if (parsed.basePath && parsed.baseKind === 'date') {
    const day =
      typeof value === 'string' || value instanceof Date
        ? calendarDateFromStorage(value)
        : ''
    if (!day)
      throw new RangeError(
        'Workflow date field did not resolve to a valid calendar date',
      )
    return day
  }
  return value
}
function baseInstant(value: unknown, timezone: string): Temporal.Instant {
  try {
    if (isCalendarDate(value)) {
      const day = Temporal.PlainDate.from(value)
      const start = day.toZonedDateTime(timezone)
      if (!start.toPlainDate().equals(day)) throw new RangeError('Skipped day')
      return start.toInstant()
    }
    if (value instanceof Date || typeof value === 'number')
      return Temporal.Instant.fromEpochMilliseconds(
        value instanceof Date ? value.getTime() : value,
      )
    if (typeof value === 'string') return Temporal.Instant.from(value)
  } catch {
    /* Keep private field contents out of parser diagnostics. */
  }
  throw new RangeError(
    'Workflow time base must be a valid calendar date or absolute instant in its timezone',
  )
}

/** Legacy days/weeks stay elapsed durations. Explicit calendar units retain local clocks. */
export function resolveWorkflowTimeDate(
  token: string,
  options: WorkflowTimeOptions = {},
): Date {
  const timezone = workflowTimeZone(options.timezone)
  const parsed = parseWorkflowTimeExpression(token)
  const original = baseInstant(baseValue(parsed, options), timezone)
  if (!parsed.unit || parsed.amount === 0)
    return new Date(original.epochMilliseconds)
  const elapsed = ELAPSED[parsed.unit]
  if (elapsed) {
    const result = new Date(
      original.epochMilliseconds +
        (parsed.direction === 'subtract' ? -1 : 1) * parsed.amount! * elapsed,
    )
    if (!Number.isFinite(result.getTime()))
      throw new RangeError('Workflow time result is out of range')
    return result
  }
  // Automatic arithmetic clamps month ends, moves gaps forward, and chooses
  // the earlier repeated clock. A zero offset preserves the original occurrence.
  const result = original
    .toZonedDateTimeISO(timezone)
    .toPlainDateTime()
    .add(calendarDuration(parsed), { overflow: 'constrain' })
    .toZonedDateTime(timezone, { disambiguation: 'compatible' })
  return new Date(result.epochMilliseconds)
}

/** A date-only base plus calendar units never needs an invented midnight instant. */
export function resolveWorkflowTimeCalendarDate(
  token: string,
  options: WorkflowTimeOptions = {},
): string {
  const timezone = workflowTimeZone(options.timezone)
  const parsed = parseWorkflowTimeExpression(token)
  const evaluation = { ...options, now: options.now ?? new Date() }
  const base = baseValue(parsed, evaluation)
  if (
    isCalendarDate(base) &&
    parsed.unit &&
    WORKFLOW_CALENDAR_UNITS.includes(parsed.unit)
  ) {
    const result = Temporal.PlainDate.from(base)
      .add(calendarDuration(parsed), { overflow: 'constrain' })
      .toString()
    if (!isCalendarDate(result))
      throw new RangeError('Workflow calendar result is out of range')
    return result
  }
  const result = Temporal.Instant.fromEpochMilliseconds(
    resolveWorkflowTimeDate(token, evaluation).getTime(),
  )
    .toZonedDateTimeISO(timezone)
    .toPlainDate()
    .toString()
  if (!isCalendarDate(result))
    throw new RangeError('Workflow calendar result is out of range')
  return result
}

/** Only inspect values which the engine interpolates; never include their contents in errors. */
export function workflowTimeTokens(value: unknown): string[] {
  const pending = [value]
  const seen = new Set<object>()
  const tokens: string[] = []
  while (pending.length) {
    const current = pending.pop()
    if (typeof current === 'string') {
      if (
        /{{\s*time\./.test(current) &&
        (current.match(/{{/g) ?? []).length !==
          (current.match(/}}/g) ?? []).length
      )
        throw new RangeError('Unfinished workflow time expression')
      for (const match of current.matchAll(/{{\s*([^}]+)\s*}}/g)) {
        const token = match[1].trim()
        if (isWorkflowTimeExpression(token)) tokens.push(token)
      }
    } else if (current && typeof current === 'object') {
      if (seen.has(current)) continue
      seen.add(current)
      for (const child of Object.values(current)) pending.push(child)
    }
  }
  return tokens
}
export function assertWorkflowTimeTokens(value: unknown): void {
  for (const token of workflowTimeTokens(value))
    parseWorkflowTimeExpression(token)
}
